import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MDBIcon, MDBPopover } from 'mdb-react-ui-kit'

import './ToolTip.scss'

const ToolTip = ({ label, icon, content, placement, iconPosition, isVisible, className, iconColor, tag }) => {
  placement = placement ? placement : 'top'

  const [isOpen, setIsOpen] = useState(!!isVisible)
  const [boundary, setBoundary] = useState(undefined)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!isOpen || !rootRef.current) return
    let found
    for (let el = rootRef.current.parentElement; el && el !== document.body; el = el.parentElement) {
      const style = getComputedStyle(el)
      if (/(auto|scroll|hidden)/.test(`${style.overflowX}${style.overflowY}`)) {
        found = el
        break
      }
    }
    setBoundary(found)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined
    const onDocumentMouseDown = evt => {
      if (rootRef.current && !rootRef.current.contains(evt.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onDocumentMouseDown)
    return () => document.removeEventListener('mousedown', onDocumentMouseDown)
  }, [isOpen])

  const iconName = name =>
    String(name)
      .trim()
      .replace(/^fa[srlbd]\s+/, '')
      .replace(/^fa-/, '')

  const renderIcon = ({ style }) => {
    style = style && typeof style === 'object' ? style : {}

    if (typeof icon === 'string') return <MDBIcon fas icon={iconName(icon)} style={style} />

    if (icon !== false) return <MDBIcon fas icon='question' style={style} />
  }

  const renderLabel = ({ style }) => {
    style = style && typeof style === 'object' ? style : {}

    if (typeof label === 'string') return <span style={style}>{label}</span>

    if (label) return label
  }

  const renderTrigger = () => {
    if (iconPosition === 'left')
      return (
        <>
          {renderIcon({
            style: {
              marginRight: '3px',
              ...(iconColor ? { color: iconColor } : {}),
            },
          })}
          {renderLabel({
            style: { ...(iconColor ? { color: iconColor } : {}) },
          })}
        </>
      )

    return (
      <>
        {renderLabel({
          style: {
            marginRight: '3px',
            ...(iconColor ? { color: iconColor } : {}),
          },
        })}
        {renderIcon({ style: { ...(iconColor ? { color: iconColor } : {}) } })}
      </>
    )
  }

  useEffect(() => {
    if (!isOpen || !rootRef.current) return undefined
    const position = () => {
      const root = rootRef.current
      if (!root) return
      const bubble = root.querySelector('.ToolTipComponent')
      const trigger = root.querySelector('.ui-tooltip-trigger')
      const arrow = bubble && bubble.querySelector('.ui-tooltip-arrow')
      if (!bubble || !trigger || !arrow) return
      const b = bubble.getBoundingClientRect()
      const t = trigger.getBoundingClientRect()
      const isVertical = /^(top|bottom)/.test(bubble.getAttribute('data-popper-placement') || 'top')
      if (isVertical) {
        const limit = Math.max(b.width - arrow.offsetWidth - 2, 2)
        const left = Math.min(Math.max(t.left + t.width / 2 - b.left - arrow.offsetWidth / 2, 2), limit)
        arrow.style.left = `${Math.round(left)}px`
        arrow.style.top = ''
      } else {
        const limit = Math.max(b.height - arrow.offsetHeight - 2, 2)
        const top = Math.min(Math.max(t.top + t.height / 2 - b.top - arrow.offsetHeight / 2, 2), limit)
        arrow.style.top = `${Math.round(top)}px`
        arrow.style.left = ''
      }
    }
    let frame
    let observer
    const attach = () => {
      const bubble = rootRef.current && rootRef.current.querySelector('.ToolTipComponent')
      if (!bubble) {
        frame = requestAnimationFrame(attach)
        return
      }
      observer = new MutationObserver(position)
      observer.observe(bubble, { attributes: true, attributeFilter: ['style', 'data-popper-placement'] })
      position()
    }
    frame = requestAnimationFrame(attach)
    window.addEventListener('scroll', position, true)
    window.addEventListener('resize', position)
    return () => {
      cancelAnimationFrame(frame)
      if (observer) observer.disconnect()
      window.removeEventListener('scroll', position, true)
      window.removeEventListener('resize', position)
    }
  }, [isOpen])

  const popperOptions = useMemo(
    () => ({
      modifiers: [
        { name: 'preventOverflow', options: { boundary, padding: 5 } },
        { name: 'offset', options: { offset: [0, 5] } },
      ],
    }),
    [boundary]
  )

  return (
    <span className='ui-tooltip-root' ref={rootRef}>
      <MDBPopover
        tag={tag ?? 'span'}
        title='Details...'
        placement={placement}
        options={popperOptions}
        disablePortal
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        btnClassName='ui-tooltip-trigger'
        btnChildren={renderTrigger()}
        className={['ToolTipComponent', 'px-2', className].filter(Boolean).join(' ')}
      >
        <div className='ui-tooltip-content'>{content}</div>
        <span className='ui-tooltip-arrow' data-popper-arrow />
      </MDBPopover>
    </span>
  )
}

export default observer(ToolTip)
